import { z } from "zod";

// Firebase configuration schema
const FirebaseConfigSchema = z.object({
  apiKey: z.string().min(1, "Firebase API key is required"),
  authDomain: z.string().min(1, "Firebase auth domain is required"),
  projectId: z.string().min(1, "Firebase project ID is required"),
  storageBucket: z.string().min(1, "Firebase storage bucket is required"),
  messagingSenderId: z
    .string()
    .min(1, "Firebase messaging sender ID is required"),
  appId: z.string().min(1, "Firebase app ID is required"),
  webClientId: z.string().min(1, "Firebase web client ID is required"),
  // Optional fields
  databaseURL: z.string().optional(),
  measurementId: z.string().optional()
});

// Serenity API configuration schema
const SerenityApiConfigSchema = z.object({
  hostPort: z.string().min(1, "API host:port is required")
});

// Logging configuration schema
const LoggingConfigSchema = z.object({
  level: z.enum(["DEBUG", "INFO", "WARN", "ERROR", "NONE"])
});

// UI configuration schema
const UIConfigSchema = z.object({
  showInputTranscript: z.boolean().default(false)
});

// Sentry / observability configuration schema. DSN is optional — when unset
// initSentry() no-ops, which keeps dev builds and tests free of Sentry wiring.
const SentryConfigSchema = z.object({
  dsn: z.string().optional(),
  environment: z.enum(["DEV", "STAGING", "PROD", "TEST", "NONE"])
});

// Main app configuration schema. `release` is top-level (not inside sentry)
// because it identifies the running code, not just Sentry tagging — it'll
// also feed feedback `clientVersion`, log fields, and any future runtime
// version display. Set at build time via EXPO_PUBLIC_RELEASE; unset locally.
// See common.justfile for the BUILD vs RELEASE convention.
const AppConfigSchema = z.object({
  firebase: FirebaseConfigSchema,
  serenityApi: SerenityApiConfigSchema,
  logging: LoggingConfigSchema,
  ui: UIConfigSchema,
  sentry: SentryConfigSchema,
  release: z.string().optional()
});

// Type definitions
export type FirebaseConfig = z.infer<typeof FirebaseConfigSchema>;
export type SerenityApiConfig = z.infer<typeof SerenityApiConfigSchema>;
export type LoggingConfig = z.infer<typeof LoggingConfigSchema>;
export type UIConfig = z.infer<typeof UIConfigSchema>;
export type SentryConfig = z.infer<typeof SentryConfigSchema>;
export type AppConfig = z.infer<typeof AppConfigSchema>;

function createAppConfig(): AppConfig {
  const rawConfig = {
    firebase: {
      apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
      authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
      databaseURL: process.env.EXPO_PUBLIC_FIREBASE_DATABASE_URL,
      measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
      webClientId: process.env.EXPO_PUBLIC_FIREBASE_WEB_CLIENT_ID
    },
    serenityApi: {
      hostPort: process.env.EXPO_PUBLIC_SERENITY_API
    },
    logging: {
      level: process.env.EXPO_PUBLIC_LOG_LEVEL || "INFO"
    },
    ui: {
      showInputTranscript: true
    },
    sentry: {
      // `||` collapses an empty `EXPO_PUBLIC_SENTRY_DSN=` into `undefined`,
      // so the optional schema treats blank-in-dev as "no DSN".
      dsn: process.env.EXPO_PUBLIC_SENTRY_DSN || undefined,
      environment: process.env.EXPO_PUBLIC_ENV || (__DEV__ ? "DEV" : "PROD")
    },
    release: process.env.EXPO_PUBLIC_RELEASE || undefined
  };

  // Zod will handle validation and provide clear error messages
  return AppConfigSchema.parse(rawConfig);
}

// Create and validate the config
const config = createAppConfig();

// Export the full config
export default config;
